import type { Booking, User } from '@/types';
import { fromE164India } from '@/shared/utils/phone';
import { formatBookingWhen } from '@/shared/utils/bookingWhen';

export interface SosGeoLocation {
  lat: number;
  lng: number;
  accuracy?: number;
}

/** Digits-only E.164 without leading + (for wa.me). */
export function phoneDigitsForWa(phone: string): string {
  return phone.replace(/\D/g, '');
}

export function maskPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return phone;
  const last4 = digits.slice(-4);
  if (digits.startsWith('91') && digits.length >= 12) {
    return `+91 ••••••${last4}`;
  }
  return `••••••${last4}`;
}

export function buildTelHref(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  return `tel:+${digits}`;
}

export function buildSmsHref(phone: string, body: string): string {
  const digits = phone.replace(/\D/g, '');
  return `sms:+${digits}?body=${encodeURIComponent(body)}`;
}

export function buildWhatsAppHref(phone: string, text: string): string {
  const digits = phoneDigitsForWa(phone);
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export function buildMapsHref(location: SosGeoLocation): string {
  return `https://maps.google.com/?q=${location.lat},${location.lng}`;
}

export function buildSosShareMessage(
  booking: Booking,
  user: User | null | undefined,
  location?: SosGeoLocation | null
): string {
  const destination =
    booking.destinationLabel ?? booking.destinationName ?? 'my destination';
  const guideName = booking.guide?.fullName ?? 'my guide';
  const travelerPhone = user?.contactPhone ?? user?.phone;
  const reference = booking.id.slice(-8).toUpperCase();
  const travelDate = booking.travelDate ? formatBookingWhen(booking) : '';

  const lines = [
    'SOS — I need help on my Trplin trip.',
    `Booking ref: #${reference}`,
    `Place: ${destination}`,
  ];

  if (travelDate) {
    lines.push(`Trip: ${travelDate}`);
  }

  lines.push(`Guide: ${guideName}`);

  if (booking.guide?.phone) {
    lines.push(`Guide phone: ${booking.guide.phone}`);
  }

  if (travelerPhone) {
    lines.push(`My phone: ${travelerPhone}`);
  }

  if (location) {
    lines.push(`My location: ${buildMapsHref(location)}`);
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    lines.push(`Trip page: ${window.location.origin}/bookings/${booking.id}/confirmation`);
  }

  lines.push('Please call me if you can.');
  return lines.join('\n');
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy copy.
    }
  }

  if (typeof document === 'undefined') return false;

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.width = '1px';
    textarea.style.height = '1px';
    textarea.style.padding = '0';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.boxShadow = 'none';
    textarea.style.background = 'transparent';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export type NativeShareResult = 'shared' | 'cancelled' | 'unsupported';

export async function shareTextNative(
  title: string,
  text: string
): Promise<NativeShareResult> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return 'unsupported';
  }

  try {
    await navigator.share({ title, text });
    return 'shared';
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return 'cancelled';
    }
    return 'unsupported';
  }
}

export function canUseNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export function hasEmergencyContact(user: User | null | undefined): boolean {
  return Boolean(user?.emergencyContactName?.trim() && user?.emergencyContactNumber?.trim());
}

export async function requestSosLocation(
  timeoutMs = 8000
): Promise<SosGeoLocation | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return null;
  }

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(timer);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      () => {
        window.clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 }
    );
  });
}

export function iceNationalDigits(user: User | null | undefined): string {
  return fromE164India(user?.emergencyContactNumber);
}
